import { noop } from 'lodash';
import { useSearchParams } from 'next/navigation';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { campaignPartnershipEventProps } from '@/components/SimplifiedSignup/configs/Campaigns/analytics';
import campaignMessages from '@/components/SimplifiedSignup/configs/Campaigns/messages';
import messages from '@/components/SimplifiedSignup/configs/SubscriptionExtension/messages';
import { ErrorProps } from '@/components/SimplifiedSignup/Error/types';
import { isOfferValid } from '@/components/SimplifiedSignup/utils/partnership';
import { useSimplifiedSignupModalContext } from '@/context/SimplifiedSignupModalContextProvider';
import { useAnalytics, useExperimentClient } from '@/hooks/analytics';
import {
	useCampaignConfigState,
	useDeviceState,
	usePartnerState,
	usePricesState,
	usePurchaseParamsState,
	useUserState,
} from '@/hooks/store';
import { useRouterPush } from '@/hooks/utils/useRouterPush';
import { getPrices } from '@/server/initState/getPrices';
import { withoutUnofferedFirstMonthPrices } from '@/server/initState/withoutUnofferedFirstMonthPrices';
import { setPrices } from '@/store/prices/actions';
import { setPurchaseParams } from '@/store/purchaseParams/actions';
import { getUserData } from '@/utils/apiRequest/getUserData';
import { initPurchaseParams } from '@/utils/getInitialState';

import {
	ActiveScreenConfig,
	FlowConfig,
	FlowScreenNames,
	ScreenConfig,
	SimplifiedSignupContextValue,
	SimplifiedSignupProviderProps,
} from './types';
import { shapeConfigForParams } from './utils';

export const SimplifiedSignupContext = createContext<SimplifiedSignupContextValue>({
	activeScreenKey: 'account',
	activeScreenConfig: {} as ActiveScreenConfig,
	name: '',
	pageName: '',
	screens: {},
	hasFreeTrial: true,
	isOrganicTraffic: true,
	coupon: '',
	offerCountries: null,
	offerCurrencies: null,
	plan: 'yearly',
	routeToScreen: () => {},
	logPageViewEvent: () => {},
	setFlowConfig: () => {},
	setError: () => {},
	isCheckoutSessionsEnabled: false,
});

export const useSimplifiedSignupContext = (): SimplifiedSignupContextValue =>
	useContext(SimplifiedSignupContext);

export const SimplifiedSignupContextProvider: React.FC<SimplifiedSignupProviderProps> = ({
	children,
	flowConfig: initFlowConfig,
	isCheckoutSessionsEnabled = false,
}) => {
	const [flowConfig, _setFlowConfig] = useState<FlowConfig>(initFlowConfig);

	const {
		activeScreenKey: activeScreenKeyConfig,
		coupon,
		fauxAuth = false,
		plan,
		pageName,
		name,
		screens,
		offerCountries,
		offerCurrencies,
		isWithinModal,
		shouldShowIneligibleOfferScreen,
	} = flowConfig;

	const user = useUserState();
	const prices = usePricesState();

	const purchaseParams = usePurchaseParamsState();
	const device = useDeviceState();
	const searchParams = useSearchParams();
	const { logEvent } = useAnalytics();
	const routerPush = useRouterPush();
	const partner = usePartnerState();
	const campaignConfig = useCampaignConfigState();
	const experimentClient = useExperimentClient();
	const dispatch = useDispatch();

	const isOrganicTraffic = searchParams?.get('utm_medium') !== 'paid';
	const email = searchParams?.get('email');

	const [activeScreenKey, setActiveScreenKey] = useState<FlowScreenNames>(activeScreenKeyConfig);
	const activeScreenConfig: ActiveScreenConfig = useMemo(
		() => ({ ...screens[activeScreenKey], isWithinModal }),
		[activeScreenKey, screens, isWithinModal],
	);
	const ActiveScreen = useMemo(() => activeScreenConfig.component, [activeScreenConfig]);
	const showHeader = useMemo(() => screens[activeScreenKey]?.showHeader || true, [activeScreenKey, screens]);
	const { setIsModalOpen } = useSimplifiedSignupModalContext();

	const [error, setError] = useState<ErrorProps | undefined>();

	const lastInvokedNextScreen = useRef<ScreenConfig['nextScreen']>(null);

	const coBranded = useMemo(() => flowConfig.coBranded || undefined, [flowConfig.coBranded]);
	const logo = useMemo(() => flowConfig.logo || undefined, [flowConfig.logo]);

	/**
	 * Check if the offer is valid for the current user
	 */
	const isOfferCountry = useCallback((): boolean => {
		// setting both of these to null opens up the offer to all countries and currencies
		if (!offerCurrencies && !offerCountries) return true;

		return device?.ip_country && device?.ip_country !== 'unknown'
			? (offerCountries ?? []).includes(device?.ip_country || '')
			: (offerCurrencies ?? []).includes(prices.pricing_format.currency);
	}, [device, offerCountries, offerCurrencies, prices.pricing_format.currency]);

	const setFlowConfig = useCallback(
		async (config: FlowConfig) => {
			const pricesQuery = { coupon: config.coupon ?? searchParams?.get('coupon') ?? undefined };
			const newPrices = await withoutUnofferedFirstMonthPrices({
				prices: await getPrices(pricesQuery, getUserData()),
				device,
				query: pricesQuery,
				refetch: query => getPrices(query, getUserData()),
			});
			const newPurchaseParams = initPurchaseParams({
				query: searchParams ? Object.fromEntries(searchParams.entries()) : {},
				user,
				prices: newPrices,
				device,
				routeSpecificPurchaseParams: shapeConfigForParams(config),
			});
			if (newPrices) {
				dispatch(setPrices(newPrices));
			}
			dispatch(setPurchaseParams(newPurchaseParams));
			_setFlowConfig(config);
		},
		[device, dispatch, searchParams, user],
	);

	/**
	 * Route to the next screen in the flow - this is the main method for navigating the flow
	 */

	const routeToScreen = useCallback(
		(screen: FlowScreenNames | null): void => {
			if (!screen) {
				return;
			}

			// Track exit from current screen before navigating
			logEvent({
				eventName: 'Screen : Exited',
				eventProps: {
					screen_name: activeScreenKey,
					exited_to: screen,
					source: name,
				},
			});

			setActiveScreenKey(screen);
		},
		[activeScreenKey, logEvent, name],
	);

	/**
	 * This is where the majority of the pricing logic (and bugs) can be found.
	 *
	 * This is a great area to refactor and clean up the pricing logic. My plan with Sisu 1.5 was to
	 * introduce a middleware pattern to process the configuration and handle the pricing logic on the
	 * next web server. This would allow us to have a single source of truth for pricing and reduce the
	 * complexity of the pricing logic in the client.
	 *
	 */

	/**
	 * This is the main logic for the flow navigation.
	 * It determines where the user should go based on their subscription status
	 */
	useEffect(() => {
		if (error) {
			return noop();
		}

		// // If the offer is no longer valid, inform the user and offer 40% off offer
		if (!isOfferValid(partner)) {
			return noop();
		}

		// A paid (non-bypass) campaign with no requires_code screen and no offer_code has
		// no code to purchase with and never will -- the shared monolith endpoint 400s on
		// any missing code today, regardless of requires_code. Fail immediately, before any
		// signup/login interaction, rather than only once the user is already logged in.
		if (
			campaignConfig &&
			!campaignConfig.bypass_payment &&
			!campaignConfig.requires_code &&
			!campaignConfig.offer_code
		) {
			// TODO: refactor this code to follow best practices
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setError({
				heading: campaignMessages.missingCodeErrorHeading,
				subheading: campaignMessages.missingCodeErrorSubheading,
			});
			return;
		}

		// Logged in and active Calm - send to wherever config dictates
		// Can be a function that triggers a side effect such as SheerID
		if (user?.subscription?.valid) {
			// if the user has a Calm premium account but is trying to claim a
			// partner or campaign offer, let the account page (SisuAuthForm) handle the error
			// instead of this effect's hard hasPremiumSubCallback redirect below.
			if (
				(partner || campaignConfig) &&
				activeScreenConfig.name === 'account' &&
				!activeScreenConfig.ignoreSubscriptionStatus
			) {
				noop();
			} else if (activeScreenConfig.hasPremiumSubCallback && !activeScreenConfig.ignoreSubscriptionStatus) {
				activeScreenConfig.hasPremiumSubCallback(routerPush, setIsModalOpen);
			} else if (typeof activeScreenConfig.nextScreen === 'function') {
				if (lastInvokedNextScreen.current !== activeScreenConfig.nextScreen) {
					lastInvokedNextScreen.current = activeScreenConfig.nextScreen;
					activeScreenConfig.nextScreen({
						routeToScreen,
						user,
						_searchParams: searchParams,
						setFlowConfig,
						experimentClient,
						setError,
					});
				}
			} else {
				routeToScreen(activeScreenConfig.nextScreen);
			}
		}

		// Logged in but not active Calm - send to Payment Page
		if (user && !user?.subscription?.valid) {
			if (
				campaignConfig &&
				activeScreenKey === 'account' &&
				campaignConfig.requires_code &&
				!campaignConfig.codeValidated
			) {
				// Account renders CampaignCodeEntry instead of SisuAuthForm until a code is
				// validated, but that gate only controls what Account itself renders -- this
				// effect decides the next screen before Account ever mounts, so an
				// already-logged-in free user would otherwise skip code entry entirely. Checking
				// codeValidated rather than offer_code's truthiness matters here: [slug].tsx seeds
				// offer_code straight from the URL, before CampaignCodeEntry ever validates it, so
				// a user who is already logged in on first render would otherwise race past this
				// guard and reach Payment with an unconfirmed code.
				return;
			}

			if (typeof activeScreenConfig.nextScreen === 'function') {
				const _searchParams = searchParams?.size ? searchParams : undefined;
				if (!isOfferCountry()) {
					if (shouldShowIneligibleOfferScreen) {
						setError({
							heading: messages.ineligibleErrorHeading,
							subheading: messages.ineligibleErrorSubheading,
							cta: {
								label: messages.ineligibleErrorCta,
								onClick: async routerPush => {
									await routerPush('/app');
								},
							},
						});
					} else {
						routerPush('/subscribe');
					}
				} else if (lastInvokedNextScreen.current !== activeScreenConfig.nextScreen) {
					lastInvokedNextScreen.current = activeScreenConfig.nextScreen;
					activeScreenConfig.nextScreen({
						routeToScreen,
						user,
						_searchParams,
						setFlowConfig,
						experimentClient,
						device,
						setError,
					});
				}
			} else {
				routeToScreen('payment');
			}
		}

		/**
		 * This supports the fauxAuth flow where the user is not logged in but we have their email.
		 * Presenting them with a purchase screen and allowing them to claim the offer.
		 */

		if (!user) {
			if (fauxAuth && email) {
				routeToScreen('payment');
			} else {
				routeToScreen('account');
			}
		}
	}, [
		user,
		device,
		activeScreenKey,
		activeScreenConfig,
		searchParams,
		routeToScreen,
		isOfferCountry,
		routerPush,
		partner,
		campaignConfig,
		setIsModalOpen,
		email,
		experimentClient,
		fauxAuth,
		shouldShowIneligibleOfferScreen,
		setFlowConfig,
		error,
	]);

	useEffect(() => {
		if (error) {
			// TODO: refactor this code to follow best practices
			// eslint-disable-next-line react-hooks/set-state-in-effect
			routeToScreen('error');
		}
	}, [error, routeToScreen]);

	const logPageViewEvent = useCallback(
		// TODO: refactor this code to follow best practices
		// eslint-disable-next-line react-hooks/preserve-manual-memoization
		(screen: FlowScreenNames | null) => {
			logEvent({
				eventName: 'Page : Viewed',
				eventProps: {
					page_name: `${pageName}-${screen}`,
					source: name,
					...(name === 'spotify' && {
						content_source: searchParams?.get('spotifyShowUri') ?? 'organic',
					}),
					...(!!user?.id && { calm_user_id: user.id }),
					...(campaignConfig ? campaignPartnershipEventProps(campaignConfig) : {}),
				},
			});
		},
		[campaignConfig, logEvent, name, pageName, searchParams, user?.id],
	);

	const value = useMemo(
		() => ({
			activeScreenKey,
			ActiveScreen,
			activeScreenConfig,
			coBranded,
			logo,
			name,
			pageName,
			user,
			plan,
			coupon,
			purchaseParams,
			isOfferCountry,
			offerCurrencies: flowConfig.offerCurrencies,
			offerCountries: flowConfig.offerCountries,
			screens: flowConfig.screens,
			hasFreeTrial: flowConfig.hasFreeTrial,
			showHeader,
			isOrganicTraffic,
			routeToScreen,
			logPageViewEvent,
			setFlowConfig,
			flowConfig,
			fauxAuth,
			error,
			setError,
			isCheckoutSessionsEnabled,
		}),
		[
			routeToScreen,
			activeScreenKey,
			activeScreenConfig,
			ActiveScreen,
			coBranded,
			logo,
			name,
			pageName,
			user,
			plan,
			coupon,
			purchaseParams,
			isOfferCountry,
			showHeader,
			isOrganicTraffic,
			logPageViewEvent,
			setFlowConfig,
			flowConfig,
			fauxAuth,
			error,
			isCheckoutSessionsEnabled,
		],
	);

	return <SimplifiedSignupContext.Provider value={value}>{children}</SimplifiedSignupContext.Provider>;
};
